import { ChevronDown, Search } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CircleLoader } from 'common-src/components/base';
import { getCurrentUserId } from 'common-src/features/auth';
import { ColorsNew } from 'common-src/styles';

import { CLIENT_ROOT } from 'src/constants/paths';
import { getCareOrchestrationTaskTitleOverrides } from 'src/features/careOrchestration';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useTabs from 'src/hooks/useTabs';

import {
  buildRenderableSectionItems,
  buildTaskQueuePrioritySummary,
  getLinkedTaskWaitingForTitle,
} from '../../helpers';
import {
  assignLeadTaskToMe,
  blockLeadTask,
  createPatientFromLead,
  deescalateLeadTask,
  escalateLeadTask,
  fetchLeadTaskQueue,
  markLeadTaskComplete,
  reassignLeadTask,
  sendLeadConsentForm,
  unassignLeadTask,
  unblockLeadTask,
} from '../../services/careOrchestrationApi';
import { Badge } from '../Badge';
import DisqualifyLeadModal from '../DisqualifyLeadModal';
import LeadTaskDetail from '../LeadTaskDetail';
import LeadWorkModal from '../LeadWorkModal';
import searchStyles from '../PatientTaskSearch/PatientTaskSearch.module.scss';
import TaskRow from '../TaskRow';
import styles from '../TasksView/TasksView.module.scss';
import {
  adaptLeadTask,
  groupTasksByLead,
  matchesLeadOwner,
  matchesLeadSearch,
} from './leadGrouping';

const OWNER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'my', label: 'My Tasks' },
  { key: 'unassigned', label: 'Unassigned' },
];

const STATUS_TABS = [
  { key: 'open', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'all', label: 'All' },
];

const PAGE_SIZE = 250;
const LEAD_TASK_POLL_INTERVAL_MS = 30000;

const isOpenManualTask = (task) =>
  task.stepType !== 'automatic' && ![3, 4, 5].includes(Number(task.status));

const resolveLeadLinkTask = (tasks, leadResourceId) => {
  const leadTasks = tasks.filter((task) => task.leadResourceId === leadResourceId);
  if (!leadTasks.length) return null;
  return (
    leadTasks.find((task) => task.taskKey === 'work_lead' && isOpenManualTask(task)) ||
    [...leadTasks].reverse().find(isOpenManualTask) ||
    leadTasks[leadTasks.length - 1]
  );
};

const LeadTasksView = ({
  onCountChange,
  requestedTaskId,
  requestedLeadResourceId,
  requestedClaim = false,
  onSelectedTaskChange,
}) => {
  const { showAlert, AlertType } = useAlert();
  const { onSelectTab } = useTabs();
  const currentUserId = useCustomSelector(getCurrentUserId);
  const titleOverrides = useCustomSelector(getCareOrchestrationTaskTitleOverrides) || {};
  const pendingLinkRef = useRef(
    requestedTaskId || requestedLeadResourceId
      ? {
          taskId: requestedTaskId || null,
          leadResourceId: requestedLeadResourceId || null,
          claim: !!requestedClaim,
        }
      : null,
  );

  const [rawTasks, setRawTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedTaskId, setSelectedTaskId] = useState(requestedTaskId || null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedLeads, setExpandedLeads] = useState({});
  const [expandedLinkedTasks, setExpandedLinkedTasks] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [activeAction, setActiveAction] = useState(null);
  const [workModalTaskId, setWorkModalTaskId] = useState(null);
  const [disqualifyTask, setDisqualifyTask] = useState(null);

  const requestedTaskIdRef = useRef(requestedTaskId);
  useEffect(() => {
    requestedTaskIdRef.current = requestedTaskId;
  }, [requestedTaskId]);

  const showAlertRef = useRef(showAlert);
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => {
    showAlertRef.current = showAlert;
    onCountChangeRef.current = onCountChange;
  });

  const loadTasks = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setIsLoading(true);
      try {
        const response = await fetchLeadTaskQueue({
          status: statusFilter,
          owner: 'all',
          limit: PAGE_SIZE,
        });
        let nextTasks = response?.data || [];

        const link = pendingLinkRef.current;
        if (link) {
          const alreadyLoaded = link.taskId
            ? nextTasks.some((task) => task.id === link.taskId)
            : nextTasks.some((task) => task.leadResourceId === link.leadResourceId);
          if (!alreadyLoaded) {
            const linked = await fetchLeadTaskQueue({
              status: 'all',
              owner: 'all',
              ...(link.taskId ? { taskId: link.taskId } : { leadResourceId: link.leadResourceId }),
              limit: 50,
            });
            const linkedTasks = linked?.data || [];
            const loadedIds = new Set(nextTasks.map((task) => task.id));
            nextTasks = [...linkedTasks.filter((task) => !loadedIds.has(task.id)), ...nextTasks];
          }
        }
        setRawTasks(nextTasks);
        setTotal(response?.total ?? nextTasks.length);
        onCountChangeRef.current?.(response?.total ?? nextTasks.length);
        const requested = requestedTaskIdRef.current;
        if (requested && nextTasks.some((task) => task.id === requested)) {
          setSelectedTaskId(requested);
        }
      } catch (requestError) {
        if (!silent) {
          showAlertRef.current?.(
            AlertType.Error,
            '',
            requestError.message || 'Unable to load lead tasks',
          );
        }
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [AlertType.Error, statusFilter],
  );

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    const poll = () => loadTasks({ silent: true });
    const intervalId = window.setInterval(poll, LEAD_TASK_POLL_INTERVAL_MS);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadTasks]);

  const tasks = useMemo(
    () => rawTasks.map((task) => adaptLeadTask(task, titleOverrides)).filter(Boolean),
    [rawTasks, titleOverrides],
  );

  const ownerStats = useMemo(
    () =>
      OWNER_TABS.reduce(
        (acc, tab) => ({
          ...acc,
          [tab.key]: tasks.filter((task) => matchesLeadOwner(task, tab.key, currentUserId)).length,
        }),
        {},
      ),
    [currentUserId, tasks],
  );

  const filteredTasks = useMemo(
    () =>
      tasks.filter(
        (task) =>
          matchesLeadOwner(task, ownerFilter, currentUserId) && matchesLeadSearch(task, search),
      ),
    [currentUserId, ownerFilter, search, tasks],
  );

  const leadGroups = useMemo(() => groupTasksByLead(filteredTasks), [filteredTasks]);
  const prioritySummary = useMemo(
    () => buildTaskQueuePrioritySummary(filteredTasks),
    [filteredTasks],
  );

  const selectedTask = useMemo(
    () => tasks.find((task) => task.id === selectedTaskId) || null,
    [selectedTaskId, tasks],
  );

  const selectedLeadTasks = useMemo(
    () =>
      selectedTask
        ? tasks.filter((task) => task.leadResourceId === selectedTask.leadResourceId)
        : [],
    [selectedTask, tasks],
  );

  useEffect(() => {
    const link = pendingLinkRef.current;
    if (!link || isLoading) return;
    const task = link.taskId
      ? tasks.find((candidate) => candidate.id === link.taskId)
      : resolveLeadLinkTask(tasks, link.leadResourceId);
    if (!task) return;
    pendingLinkRef.current = null;

    setSelectedTaskId(task.id);
    if (!link.claim || !isOpenManualTask(task)) return;
    const isMine = `${task.assignee ?? ''}` === `${currentUserId ?? ''}`;

    (async () => {
      let claimed = isMine;
      if (task.assignee == null) {
        try {
          await assignLeadTaskToMe(task.id);
          showAlertRef.current?.(AlertType.Success, '', 'Task claimed — it is yours.');
          claimed = true;
        } catch (requestError) {
          showAlertRef.current?.(
            AlertType.Error,
            '',
            requestError.message || 'Someone else claimed this task first.',
          );
        }
      }
      await loadTasks({ silent: true });
      if (claimed) setWorkModalTaskId(task.id);
    })();
  }, [
    AlertType.Error,
    AlertType.Success,
    currentUserId,
    isLoading,
    loadTasks,
    statusFilter,
    tasks,
  ]);

  const workModalTask = useMemo(
    () => (workModalTaskId ? tasks.find((task) => task.id === workModalTaskId) || null : null),
    [tasks, workModalTaskId],
  );

  const workModalRelatedTasks = useMemo(
    () =>
      workModalTask
        ? tasks.filter((task) => task.leadResourceId === workModalTask.leadResourceId)
        : [],
    [tasks, workModalTask],
  );

  const closeWorkModal = useCallback(() => setWorkModalTaskId(null), []);

  useEffect(() => {
    if (!selectedTask) return;
    const groupKey = `lead-${selectedTask.leadResourceId}`;
    setExpandedLeads((prev) => (prev[groupKey] ? prev : { ...prev, [groupKey]: true }));
  }, [selectedTask]);

  useEffect(() => {
    if (requestedTaskId && requestedTaskId !== selectedTaskId) {
      setSelectedTaskId(requestedTaskId);
    }
  }, [requestedTaskId, selectedTaskId]);

  const lastNotifiedTaskIdRef = useRef(null);
  useEffect(() => {
    if (!selectedTaskId || lastNotifiedTaskIdRef.current === selectedTaskId) return;
    lastNotifiedTaskIdRef.current = selectedTaskId;
    onSelectedTaskChange?.(selectedTaskId);
  }, [onSelectedTaskChange, selectedTaskId]);

  const runAction = async (actionName, action, successMessage = null) => {
    if (activeAction) return false;
    setActiveAction(actionName);
    try {
      await action();
      if (successMessage) showAlert(AlertType.Success, '', successMessage);
      await loadTasks();
      return true;
    } catch (requestError) {
      showAlert(AlertType.Error, '', requestError.message || 'Unable to update lead task');
      return false;
    } finally {
      setActiveAction(null);
    }
  };

  const handleAssignToMe = (taskId) =>
    runAction(`assign:${taskId}`, () => assignLeadTaskToMe(taskId));

  const handleMarkComplete = (task, completionPayload) =>
    runAction('complete', () => markLeadTaskComplete(task.id, completionPayload));

  const handleMarkCompleteFromModal = async (task) => {
    const done = await handleMarkComplete(task, { result: 'done', decision: 'done' });
    if (done) setWorkModalTaskId(null);
  };

  const handleReassign = (taskId, assigneeId) =>
    runAction(`reassign:${taskId}`, () => reassignLeadTask(taskId, assigneeId), 'Task reassigned.');

  const handleUnassign = (taskId) =>
    runAction(`unassign:${taskId}`, () => unassignLeadTask(taskId), 'Task released.');

  const handleEscalate = (taskId, escalationPayload) =>
    runAction(
      `escalate:${taskId}`,
      () => escalateLeadTask(taskId, escalationPayload),
      'Task escalated.',
    );

  const handleDeescalate = (taskId) =>
    runAction(`escalate:${taskId}`, () => deescalateLeadTask(taskId), 'Task de-escalated.');

  const handleBlock = (taskId, blockedReason) =>
    runAction(`block:${taskId}`, () => blockLeadTask(taskId, blockedReason), 'Task blocked.');

  const handleUnblock = (taskId) =>
    runAction(`block:${taskId}`, () => unblockLeadTask(taskId), 'Task unblocked.');

  const handleSendConsent = (task) =>
    runAction(
      'consent',
      () => sendLeadConsentForm(task.leadResourceId, { leadTaskId: task.id }),
      'Consent form sent.',
    );

  const handleCreatePatient = (task = selectedTask) =>
    runAction(
      'createPatient',
      async () => {
        const body = await createPatientFromLead(task.leadResourceId);
        if (body && body.success === false) {
          const reasons = [
            ...(body.missingFields?.length ? [`missing ${body.missingFields.join(', ')}`] : []),
            ...(body.invalidFields?.length ? [`invalid ${body.invalidFields.join(', ')}`] : []),
          ];
          throw new Error(
            reasons.length
              ? `Patient not created: ${reasons.join('; ')}.`
              : 'Patient not created: an existing patient with the same name and date of birth could not be confirmed. Add the email or phone that matches, or create the patient by hand.',
          );
        }
      },
      'Patient created.',
    );

  const handleCreatePatientFromModal = async (task) => {
    if (await handleCreatePatient(task)) setWorkModalTaskId(null);
  };

  const requestDisqualify = (task) => setDisqualifyTask(task);

  const handleDisqualify = async (task, disqualificationFields) => {
    const done = await runAction('disqualify', () =>
      markLeadTaskComplete(task.id, {
        result: 'disqualified',
        decision: 'disqualified',
        ...disqualificationFields,
      }),
    );
    if (done) setWorkModalTaskId(null);
    return done;
  };

  const toggleLead = (groupKey) => {
    setExpandedLeads((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const expandAllLeads = () => {
    setExpandedLeads(leadGroups.reduce((acc, group) => ({ ...acc, [group.key]: true }), {}));
  };

  const collapseAllLeads = () => {
    setExpandedLeads({});
  };

  const toggleLinkedTasks = (taskId) => {
    const normalizedTaskId = `${taskId || ''}`;
    if (!normalizedTaskId) return;
    setExpandedLinkedTasks((prev) => ({ ...prev, [normalizedTaskId]: !prev[normalizedTaskId] }));
  };

  const summaryLabel = `${leadGroups.length} ${leadGroups.length === 1 ? 'lead' : 'leads'} · ${
    filteredTasks.length
  } of ${total} ${total === 1 ? 'task' : 'tasks'}`;

  const renderTaskRow = (group, task) => (
    <TaskRow
      key={task.id}
      task={task}
      isLinkedChild={group.linkedTaskStructure.linkedChildIds.has(`${task.id}`)}
      linkedParentTitle={getLinkedTaskWaitingForTitle(task, group.tasks) ?? null}
      linkedChildCount={group.linkedTaskStructure.parentLinkedChildCount.get(`${task.id}`) ?? 0}
      subtaskChildCount={0}
      linkedExpanded={!!expandedLinkedTasks[`${task.id}`]}
      onToggleLinked={toggleLinkedTasks}
      selected={selectedTaskId === task.id}
      onSelect={(nextTask) => setSelectedTaskId(nextTask.id)}
      onAssignToMe={handleAssignToMe}
      isAssigning={activeAction === `assign:${task.id}`}
    />
  );

  const renderTaskItems = (group, items) =>
    items.map((item) => {
      if (item.type === 'child-group') {
        return (
          <div key={item.key} className={styles.childGroupContainer}>
            {item.items.map((childItem) => {
              if (childItem.type === 'label') {
                return (
                  <div key={childItem.key} className={styles.childSectionLabel}>
                    {childItem.label}
                  </div>
                );
              }
              if (childItem.type === 'child-group') {
                return renderTaskItems(group, [childItem])[0];
              }
              return renderTaskRow(group, childItem.task);
            })}
          </div>
        );
      }
      return renderTaskRow(group, item.task);
    });

  const renderTasksTable = (group) => (
    <div className={styles.bucketRows}>
      <div className={styles.tableHeader}>
        <span />
        <span className={styles.headerCell}>Task</span>
        <span className={styles.headerCell} />
        <span className={styles.headerCell}>Status</span>
        <span className={styles.headerCell}>Priority</span>
        <span className={`${styles.headerCell} ${styles.headerDue}`}>Due</span>
      </div>
      {renderTaskItems(
        group,
        buildRenderableSectionItems(group.tasks, group.linkedTaskStructure, expandedLinkedTasks),
      )}
    </div>
  );

  const renderLeadGroup = (group) => {
    const isExpanded = !!expandedLeads[group.key];
    const leadPatientId = group.lead?.patientId ?? null;

    return (
      <div key={group.key} className={styles.patientGroup}>
        <div className={styles.leadRowWrap}>
          <button type="button" className={styles.patientRow} onClick={() => toggleLead(group.key)}>
            <ChevronDown
              size={12}
              strokeWidth={1.8}
              className={`${styles.patientChevron} ${!isExpanded ? styles.patientChevronCollapsed : ''}`}
            />
            <span className={styles.patientMarker} />
            <div className={styles.patientMain}>
              <div className={styles.patientTopRow}>
                <span className={styles.patientName}>{group.name}</span>
                {group.lead?.phone ? (
                  <span className={styles.patientMetaMuted}>{group.lead.phone}</span>
                ) : null}
              </div>
              <div className={styles.patientMetaRow}>
                {group.lead?.email ? (
                  <span className={styles.patientMetaText}>{group.lead.email}</span>
                ) : null}
                {group.activeTask ? (
                  <span className={styles.patientMetaText}>{group.activeTask.title}</span>
                ) : null}
                {group.lead?.businessUnit ? (
                  <Badge variant="outline" className={styles.pathwayBadge}>
                    {group.lead.businessUnit}
                  </Badge>
                ) : null}
              </div>
            </div>
            <div className={styles.patientPills}>
              {group.criticalCount ? (
                <Badge variant="critical" className={styles.patientSummaryBadge}>
                  {group.criticalCount} critical
                </Badge>
              ) : null}
              {group.blockedCount ? (
                <Badge variant="critical" className={styles.patientSummaryBadge}>
                  {group.blockedCount} blocked
                </Badge>
              ) : null}
              {group.escalatedCount ? (
                <Badge variant="escalated" className={styles.patientSummaryBadge}>
                  {group.escalatedCount} escalated
                </Badge>
              ) : null}
              {group.openCount || group.doneCount ? (
                <span className={styles.summaryPillGroup}>
                  {group.openCount ? (
                    <span
                      className={`${styles.summaryPill} ${styles.summaryPillDark} ${
                        group.doneCount ? styles.summaryPillGrouped : ''
                      }`}
                    >
                      {group.openCount} open
                    </span>
                  ) : null}
                  {group.doneCount ? (
                    <span
                      className={`${styles.summaryPill} ${
                        group.openCount
                          ? `${styles.summaryPillGrouped} ${styles.summaryPillGroupedTrailing}`
                          : ''
                      }`}
                    >
                      {group.doneCount} done
                    </span>
                  ) : null}
                </span>
              ) : null}
            </div>
            <span className={styles.patientDue}>{group.nextDueLabel}</span>
          </button>
          {leadPatientId ? (
            <div className={styles.leadPatientActions}>
              <button
                type="button"
                className={styles.leadPatientLink}
                title={`Open patient #${leadPatientId} in a new tab`}
                onClick={() =>
                  onSelectTab({ path: `${CLIENT_ROOT}/patients/details/${leadPatientId}/info` })
                }
              >
                Patient #{leadPatientId} ↗
              </button>
              <button
                type="button"
                className={styles.leadPatientLink}
                title={`Add insurance for patient #${leadPatientId}`}
                onClick={() =>
                  onSelectTab({
                    path: `${CLIENT_ROOT}/patients/details/${leadPatientId}/known-plans`,
                  })
                }
              >
                Add insurance ↗
              </button>
            </div>
          ) : null}
        </div>

        {isExpanded ? (
          <div className={styles.patientExpanded}>
            <div className={styles.bucketSection}>
              <div className={styles.bucketHeader}>
                <span className={`${styles.bucketIndex} ${styles.bucketIndexDark}`}>1</span>
                <span className={styles.bucketLabel}>Workflow</span>
                <span className={styles.bucketCount}>{group.tasks.length}</span>
              </div>
              {renderTasksTable(group)}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div className={styles.filters}>
        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            {OWNER_TABS.map((ownerTab) => (
              <button
                key={ownerTab.key}
                type="button"
                onClick={() => setOwnerFilter(ownerTab.key)}
                className={`${styles.filterButton} ${
                  ownerFilter === ownerTab.key ? styles.filterActive : ''
                }`}
              >
                {ownerTab.label}
                <span className={styles.filterCount}>{ownerStats[ownerTab.key] ?? 0}</span>
              </button>
            ))}
          </div>
          <div className={styles.filterDivider} />
          <div className={styles.filterGroup}>
            {STATUS_TABS.map((statusTab) => (
              <button
                key={statusTab.key}
                type="button"
                onClick={() => setStatusFilter(statusTab.key)}
                className={`${styles.filterButton} ${
                  statusFilter === statusTab.key ? styles.filterActive : ''
                }`}
              >
                {statusTab.label}
              </button>
            ))}
          </div>
          <div className={styles.filterActions}>
            <div className={searchStyles.patientSearchWrap}>
              <div className={searchStyles.patientSearchInput}>
                <Search className={searchStyles.patientSearchIcon} strokeWidth={2} />
                <input
                  type="text"
                  className={searchStyles.patientSearchNativeInput}
                  aria-label="Search lead tasks"
                  placeholder="Search leads or tasks"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                {search ? (
                  <button
                    type="button"
                    className={searchStyles.patientSearchClearIcon}
                    aria-label="Clear search"
                    onClick={() => setSearch('')}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={expandAllLeads}
              className={styles.filterActionButton}
              disabled={!leadGroups.length}
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAllLeads}
              className={styles.filterActionButton}
              disabled={!leadGroups.length}
            >
              Collapse
            </button>
          </div>
        </div>
      </div>

      <div className={`${styles.layout} ${selectedTask ? styles.layoutWithDetail : ''}`}>
        <div className={styles.listCard}>
          <div className={styles.summaryBar}>
            <div className={styles.summaryMeta}>
              <span className={styles.summaryText}>{summaryLabel}</span>
              <div className={styles.prioritySummary}>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryCritical}`}>
                  Critical: {prioritySummary.critical}
                </span>
                <span className={styles.prioritySummaryDivider}>·</span>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryHigh}`}>
                  High: {prioritySummary.high}
                </span>
                <span className={styles.prioritySummaryDivider}>·</span>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryMedium}`}>
                  Medium: {prioritySummary.medium}
                </span>
                <span className={styles.prioritySummaryDivider}>·</span>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryLow}`}>
                  Low: {prioritySummary.low}
                </span>
              </div>
            </div>
            <div className={styles.summaryActions}>
              <button
                type="button"
                className={styles.filterActionButton}
                onClick={() => loadTasks()}
                disabled={isLoading}
              >
                {isLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          {isLoading && (
            <div className={styles.loaderWrap}>
              <CircleLoader color={ColorsNew.darkGreen} strokeWidth={2} circleRadius={12} />
            </div>
          )}

          {!isLoading && !leadGroups.length && (
            <div className={styles.emptyState}>
              {search || ownerFilter !== 'all' ? 'No lead tasks match filters' : 'No lead tasks.'}
            </div>
          )}

          {!isLoading && leadGroups.map(renderLeadGroup)}
        </div>

        {selectedTask && (
          <div className={styles.detailPanel}>
            <LeadTaskDetail
              task={selectedTask}
              relatedTasks={selectedLeadTasks}
              currentUserId={currentUserId}
              onClose={() => setSelectedTaskId(null)}
              onAssignToMe={handleAssignToMe}
              onReassign={handleReassign}
              isReassigning={`${activeAction}`.startsWith('reassign:')}
              isAssigning={activeAction === `assign:${selectedTask.id}`}
              onUnassign={handleUnassign}
              isUnassigning={activeAction === `unassign:${selectedTask.id}`}
              onEscalate={handleEscalate}
              onDeescalate={handleDeescalate}
              isEscalating={activeAction === `escalate:${selectedTask.id}`}
              onBlock={handleBlock}
              onUnblock={handleUnblock}
              isBlocking={activeAction === `block:${selectedTask.id}`}
              onMarkComplete={handleMarkComplete}
              isMarking={activeAction === 'complete'}
              onSendConsent={handleSendConsent}
              isSendingConsent={activeAction === 'consent'}
              onCreatePatient={handleCreatePatient}
              isCreatingPatient={activeAction === 'createPatient'}
              onDisqualify={requestDisqualify}
              onLeadUpdated={() => loadTasks({ silent: true })}
              onOpenWorkModal={(task) => setWorkModalTaskId(task.id)}
            />
          </div>
        )}
      </div>

      {workModalTask && (
        <LeadWorkModal
          task={workModalTask}
          relatedTasks={workModalRelatedTasks}
          isClaimedByYou={`${workModalTask.assignee ?? ''}` === `${currentUserId ?? ''}`}
          onClose={closeWorkModal}
          onLeadUpdated={() => loadTasks({ silent: true })}
          onSendConsent={handleSendConsent}
          isSendingConsent={activeAction === 'consent'}
          onDisqualify={requestDisqualify}
          isDisqualifying={activeAction === 'disqualify'}
          onCreatePatient={handleCreatePatientFromModal}
          isCreatingPatient={activeAction === 'createPatient'}
          onMarkComplete={handleMarkCompleteFromModal}
          isMarking={activeAction === 'complete'}
        />
      )}

      {disqualifyTask && (
        <DisqualifyLeadModal
          leadName={disqualifyTask.patient?.name || 'This lead'}
          lead={disqualifyTask.lead}
          onClose={() => setDisqualifyTask(null)}
          onConfirm={(disqualificationFields) =>
            handleDisqualify(disqualifyTask, disqualificationFields)
          }
          isSubmitting={activeAction === 'disqualify'}
        />
      )}
    </>
  );
};

LeadTasksView.propTypes = {
  onCountChange: PropTypes.func,
  requestedTaskId: PropTypes.string,
  requestedLeadResourceId: PropTypes.string,
  requestedClaim: PropTypes.bool,
  onSelectedTaskChange: PropTypes.func,
};

export default LeadTasksView;
